/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { useApi } from '@/hooks'

import type { ShipmentRecord } from './types'

/**
 * The shipments a tracker is already committed to (GET /shipments defaults to
 * planned + active). Completed and cancelled ones are deliberately excluded —
 * they do not stop the device being used again.
 */
export const useTrackerShipments = (companyId: number | null, trackerId: number | null) => {
  const { api } = useApi()
  const [shipments, setShipments] = useState<ShipmentRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!companyId || !trackerId) {
      setShipments([])
      setError('')
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError('')
    api
      .get<ShipmentRecord[]>({
        path: `/shipments?companyId=${companyId}&trackerId=${trackerId}`,
        origin: 'ShipmentForm#trackerShipments',
      })
      .then((response) => {
        if (cancelled) return
        if (response.error) {
          setShipments([])
          setError(response.message || 'Could not check this device for existing shipments')
          return
        }
        setShipments(response.data ?? [])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [api, companyId, trackerId])

  return { shipments, isLoading, error }
}
